import cloneDeep from 'lodash.clonedeep';
import { transformFields } from '@core/utils/methods';
import { SCOPING_SECTORS_VERSIONS } from '@models/constants';
import { JurisdictionActions } from '@store/actions';
import { browseReducer } from '../../core/utils/methods/browse-sorter-reducer';
import { createReducer, on } from '../../core/utils/store';
import {
  AddPermitTypeData,
  JurisdictionBrowse,
  JurisdictionDetails,
  JurisdictionDetailsContacts,
  JurisdictionDetailsPermitting,
  JurisdictionDetailsPermittingType,
  JurisdictionDetailsZoning,
  JurisdictionModalValues,
  JurisdictionOption,
  JurisdictionPermittingData,
  JurisdictionSummaryZoning,
  JurisdictionZoningData,
  OptionProps,
  PaginatedResponse,
  PayloadAndState,
  PermittingBrowse,
  PermittingContactsData,
  PermittingDetails,
  PermittingDetailsJurisdictionPermitting,
  PermittingDetailsJurisdictionZoning,
  PermittingDetailsLeasingInfo,
  PermittingDetailsSiteInfo,
  PermittingFinancialState,
  PermittingJurisdictionAuditBody,
  PermittingJurisdictionAuditDataState,
  PermittingProjectSummaryData,
  PermittingScopingSummaryData,
  PermittingSofState,
  PermittingSummaryState,
  PermittingWorkflowBody,
  PermittingWorkflowData,
  PermittingWorkflowState,
} from '../../models/interfaces';
import {
  JurisdictionPermittingSectionType,
  RemovePermitTypeData,
} from '../../models/types';
import { PermittingActions } from '../actions/permitting.actions';
import {
  generateId,
  getOtherEquipmentInfo,
  getProcessedPermitTypes,
  getScopingSectorsInfo,
} from './helpers';

export interface PermittingState {
  browseData: PaginatedResponse<PermittingBrowse>;
  jurisdictionList: JurisdictionOption[];
  detailsData: PermittingDetails;
  jurisdictionBrowseData: PaginatedResponse<JurisdictionBrowse>;
  jurisdictionDetailsData: JurisdictionDetails;
  permittingUsersOptions: OptionProps[];
  isFetching: boolean;
  errors: boolean;
  originalPermitTypeTableData: {
    [permittingId: string]: JurisdictionDetailsPermittingType[];
  };
  currentPermitTypeTableData: {
    [permittingId: string]: JurisdictionDetailsPermittingType[];
  };
  loadingTabs: Record<string, boolean>;
  loadedJurisdictionTabs: Record<string, boolean>;
  tabData: {
    summary: PermittingSummaryState;
    workflow: PermittingWorkflowState;
    contacts: PermittingContactsData;
    jurisdictionAudit: PermittingJurisdictionAuditDataState;
    sof: PermittingSofState;
    financial: PermittingFinancialState;
  };
}

const initialState: PermittingState = {
  browseData: { items: [], total: 0 },
  jurisdictionList: [],
  detailsData: {} as PermittingDetails,
  jurisdictionBrowseData: { items: [], total: 0 },
  jurisdictionDetailsData: {} as JurisdictionDetails,
  permittingUsersOptions: [],
  isFetching: false,
  errors: false,
  originalPermitTypeTableData: {},
  currentPermitTypeTableData: {},
  loadingTabs: { workflow: true },
  loadedJurisdictionTabs: {},
  tabData: {
    summary: {
      site: {} as PermittingDetailsSiteInfo,
      leasing: {} as PermittingDetailsLeasingInfo,
      project: {} as PermittingProjectSummaryData,
      scoping: {} as PermittingScopingSummaryData,
      loaded: false,
    },
    workflow: {
      projectPermittingNote: '',
      jurisdiction: null,
      permittingAgent: '',
      BPExpiration: null,
      loaded: false,
      inactivePermittingUsers: [],
    },
    contacts: { contacts: [], loaded: false },
    jurisdictionAudit: {
      loaded: false,
      id: '',
      jurisdictionName: '',
      jurisdictionAuditBy: null,
      zoning: {} as PermittingDetailsJurisdictionZoning,
      permitting: {} as PermittingDetailsJurisdictionPermitting,
    },
    sof: {
      loaded: false,
      site: {} as PermittingSofState['site'],
      project: {} as PermittingSofState['project'],
      scoping: {} as PermittingSofState['scoping'],
    },
    financial: {
      loaded: false,
      siteFees: [],
      servicesRequired: [],
    },
  },
};

export const reducer = createReducer(
  initialState,
  // GET PERMITTING BROWSE DATA
  on(PermittingActions.getPermittingBrowseData, browseReducer('browseData')),
  // get jurisdiction list
  on(
    PermittingActions.getJurisdictionList,
    ({
      payload: jurisdictionList,
    }: PayloadAndState<JurisdictionOption[], PermittingState>) => ({
      jurisdictionList,
    }),
  ),
  // get permitting users
  on(
    PermittingActions.getPermittingUsersOptions,
    ({
      payload: permittingUsersOptions,
    }: PayloadAndState<OptionProps[], PermittingState>) => ({
      permittingUsersOptions,
    }),
  ),
  // Set tab loading state
  on(PermittingActions.setTabLoading, ({ payload, state }) => ({
    ...state,
    loadingTabs: {
      ...state.loadingTabs,
      [payload.tabName]: payload.isLoading,
    },
  })),

  // reset tab data
  on(JurisdictionActions.resetTabsData, ({ state }): PermittingState => {
    return {
      ...state,
      jurisdictionDetailsData: initialState.jurisdictionDetailsData,
      loadedJurisdictionTabs: {},
      loadingTabs: {},
      originalPermitTypeTableData: {},
      currentPermitTypeTableData: {},
    };
  }),

  on(PermittingActions.resetTabsData, ({ state }): PermittingState => {
    return {
      ...state,
      tabData: initialState.tabData,
      loadingTabs: {},
      originalPermitTypeTableData: {},
      currentPermitTypeTableData: {},
    };
  }),

  // Load summary tab data
  on(PermittingActions.getSummaryTabData, ({ payload, state }) => ({
    ...state,
    tabData: {
      ...state.tabData,
      summary: {
        ...payload,
        loaded: true,
      },
    },
  })),

  // Load workflow tab data
  on(
    PermittingActions.getWorkflowTabData,
    ({
      payload: workflowData,
      state,
    }: PayloadAndState<PermittingWorkflowData, PermittingState>) => {
      const permittingTypes =
        workflowData?.jurisdiction?.permitting?.permittingTypes;
      const processedPermitTypes = permittingTypes
        ? getProcessedPermitTypes(permittingTypes)
        : [];

      const permittingId = workflowData?.jurisdiction?.permitting?.id || '';

      const processedWorkflowData = {
        ...workflowData,
        permittingAgent: workflowData?.permittingAgent?.id,
        loaded: true,
      };

      return {
        tabData: {
          ...state.tabData,
          workflow: processedWorkflowData,
        },
        loadingTabs: {
          ...state.loadingTabs,
          workflow: false,
        },
        originalPermitTypeTableData: {
          ...state.originalPermitTypeTableData,
          [permittingId]: processedPermitTypes,
        },
        currentPermitTypeTableData: {
          ...state.currentPermitTypeTableData,
          [permittingId]: processedPermitTypes,
        },
      };
    },
  ),

  // Load contacts tab data
  on(PermittingActions.getContactsTabData, ({ payload, state }) => ({
    ...state,
    loadingTabs: { ...state.loadingTabs, contacts: false },
    tabData: {
      ...state.tabData,
      contacts: {
        contacts: payload,
        loaded: true,
      },
    },
  })),

  // Load sof tab data
  on(PermittingActions.getSofTabData, ({ payload, state }) => {
    const otherEquipmentInfo = getOtherEquipmentInfo(
      payload.scoping.otherEquipments ?? [],
    );

    const scopingSectorsInfo = payload.scoping.scopingSectorsVersions?.length
      ? getScopingSectorsInfo(payload.scoping.scopingSectorsVersions)
      : {
          versions: SCOPING_SECTORS_VERSIONS,
          equipmentTypeNames: [],
        };

    return {
      ...state,
      loadingTabs: { ...state.loadingTabs, sof: false },
      tabData: {
        ...state.tabData,
        sof: {
          ...payload,
          project: transformFields(payload.project, ['previousMountModels']),
          scoping: {
            ...payload.scoping,
            scopingSectorsVersions: scopingSectorsInfo?.versions,
            otherEquipments: otherEquipmentInfo?.otherEquipments || [],
            equipmentTypeNames: [
              ...(otherEquipmentInfo?.equipmentTypeNames || []),
              ...(scopingSectorsInfo?.equipmentTypeNames || []),
            ],
          },

          loaded: true,
        },
      },
    };
  }),

  // Load financial tab data
  on(PermittingActions.getFinancialTabData, ({ payload, state }) => ({
    ...state,
    loadingTabs: { ...state.loadingTabs, financial: false },
    tabData: {
      ...state.tabData,
      financial: {
        ...payload,
        loaded: true,
      },
    },
  })),

  // Load jurisdiction audit tab data
  on(PermittingActions.getJurisdictionAuditTabData, ({ payload, state }) => {
    const permittingTypes = payload?.permitting?.permittingTypes;
    const processedPermitTypes = permittingTypes
      ? getProcessedPermitTypes(permittingTypes)
      : [];

    const permittingId = payload?.permitting?.id || '';

    const processedWorkflowData = {
      ...payload,

      loaded: true,
    };

    return {
      tabData: {
        ...state.tabData,
        jurisdictionAudit: processedWorkflowData,
      },
      loadingTabs: {
        ...state.loadingTabs,
        jurisdictionAudit: false,
      },
      originalPermitTypeTableData: {
        ...state.originalPermitTypeTableData,
        [permittingId]: processedPermitTypes,
      },
      currentPermitTypeTableData: {
        ...state.currentPermitTypeTableData,
        [permittingId]: processedPermitTypes,
      },
    };
  }),

  // reset permitting types
  on(
    PermittingActions.resetPermittingTypesAction,
    ({
      payload: permittingIds,
      state: { currentPermitTypeTableData, originalPermitTypeTableData },
    }: PayloadAndState<string[], PermittingState>) => {
      const result = cloneDeep(currentPermitTypeTableData);

      permittingIds.forEach((id: string) => {
        result[id] = cloneDeep(originalPermitTypeTableData?.[id] || []);
      });

      return {
        currentPermitTypeTableData: result,
      };
    },
  ),
  // set permitting types
  on(
    PermittingActions.setPermittingTypesAction,
    ({
      payload: permittingIds,
      state: { currentPermitTypeTableData, originalPermitTypeTableData },
    }: PayloadAndState<string[], PermittingState>) => {
      const result = cloneDeep(originalPermitTypeTableData);

      permittingIds.forEach((id: string) => {
        result[id] = getProcessedPermitTypes(currentPermitTypeTableData[id]);
      });

      return {
        originalPermitTypeTableData: result,
        currentPermitTypeTableData: result,
      };
    },
  ),
  // add permitting type
  on(
    PermittingActions.permittingAddPermittingType,
    ({
      payload: { data, permittingId },
      state: { currentPermitTypeTableData },
    }: PayloadAndState<AddPermitTypeData, PermittingState>) => ({
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [permittingId]: [
          ...(currentPermitTypeTableData[permittingId] || []),
          { ...data, generatedId: generateId() },
        ],
      },
    }),
  ),
  // update permitting type
  on(
    PermittingActions.permittingUpdatePermittingType,
    ({
      payload: { data, permittingId },
      state: { currentPermitTypeTableData },
    }: PayloadAndState<AddPermitTypeData, PermittingState>) => ({
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [permittingId]: [
          ...(currentPermitTypeTableData[permittingId] || []).filter(
            (permitType: JurisdictionDetailsPermittingType) =>
              permitType.generatedId !== data.generatedId,
          ),
          data,
        ],
      },
    }),
  ),
  // remove permitting type
  on(
    PermittingActions.permittingRemovePermittingType,
    ({
      payload: { permittingTypeId, permittingId },
      state: { currentPermitTypeTableData },
    }: PayloadAndState<RemovePermitTypeData, PermittingState>) => ({
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [permittingId]: (currentPermitTypeTableData[permittingId] || []).filter(
          (permitType: JurisdictionDetailsPermittingType) =>
            permitType.generatedId !== permittingTypeId,
        ),
      },
    }),
  ),
  // update jurisdiction audit data
  on(
    PermittingActions.permittingUpdateJurisdictionAuditData,
    ({
      payload: {
        summarySection: { id: jurisdictionId, ...jurisdiction },
        zoningSection,
        permittingSection,
      },
      state,
    }: PayloadAndState<PermittingJurisdictionAuditBody, PermittingState>) => {
      const { id: zoningId, ...zoning } = zoningSection ?? {};
      const { id: permittingId, ...permitting } = permittingSection ?? {};

      return {
        tabData: {
          ...state.tabData,
          jurisdictionAudit: {
            ...state.tabData.jurisdictionAudit,
            ...jurisdiction,
            zoning: {
              ...state.tabData.jurisdictionAudit.zoning,
              ...zoning,
            },
            permitting: {
              ...state.tabData.jurisdictionAudit.permitting,
              ...permitting,
            },
            loaded: true,
          },
        },
      };
    },
  ),
  // update permittting workflow permittingFields
  on(
    PermittingActions.permittingUpdateWorkflowData,
    ({
      state,
      payload: { projectPermittingNote, permittingAgent, BPExpiration },
    }: PayloadAndState<PermittingWorkflowBody, PermittingState>) => {
      return {
        tabData: {
          ...state.tabData,
          loadingTabs: {
            ...state.loadingTabs,
            workflow: false,
          },
          workflow: {
            ...state.tabData.workflow,
            loaded: true,
            projectPermittingNote:
              projectPermittingNote ??
              state.tabData.workflow.projectPermittingNote,
            permittingAgent:
              permittingAgent ?? state.tabData.workflow.permittingAgent,
            BPExpiration: BPExpiration ?? state.tabData.workflow.BPExpiration,
          },
        },
      };
    },
  ),
  // JURISDICTION
  // get jurisdiction browse data
  on(
    JurisdictionActions.getJurisdictionBrowseData,
    ({
      payload: jurisdictionBrowseData,
    }: PayloadAndState<
      PaginatedResponse<JurisdictionBrowse>,
      PermittingState
    >) => ({
      jurisdictionBrowseData,
    }),
  ),
  // add jurisdiction
  on(
    JurisdictionActions.addJurisdiction,
    ({
      payload: jurisdictionBrowseData,
    }: PayloadAndState<
      PaginatedResponse<JurisdictionBrowse>,
      PermittingState
    >) => ({
      jurisdictionBrowseData,
    }),
  ),

  // ZONING PANEL
  // add jurisdiction zoning panel

  // get jurisdiction zoning details data
  on(
    JurisdictionActions.getJurisdictionZoning,
    ({
      state,
      payload,
    }: PayloadAndState<JurisdictionSummaryZoning, PermittingState>) => {
      return {
        jurisdictionDetailsData: {
          ...state.jurisdictionDetailsData,
          ...payload,
        },
      };
    },
  ),

  on(
    JurisdictionActions.addJurisdictionZoningPanel,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionDetailsZoning, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        zoning: [...jurisdictionDetailsData.zoning, payload],
      },
    }),
  ),
  // update jurisdiction zoning panel
  on(
    JurisdictionActions.updateJurisdictionZoningPanel,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionModalValues, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        zoning: jurisdictionDetailsData.zoning.map(
          (zoning: JurisdictionDetailsZoning) =>
            zoning.id !== payload.id
              ? zoning
              : {
                  ...zoning,
                  ...payload,
                },
        ),
      },
    }),
  ),
  // remove jurisdiction zoning panel
  on(
    JurisdictionActions.removeJurisdictionZoningPanel,
    ({
      payload: id,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<string, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        zoning: [
          ...jurisdictionDetailsData.zoning.filter(
            (zoning: JurisdictionDetailsZoning) => zoning.id !== id,
          ),
        ],
      },
    }),
  ),
  // JURISDICTION ZONING
  // update jurisdiction zoning
  on(
    JurisdictionActions.updateJurisdictionZoning,
    ({
      payload: { summarySection, zoningSection },
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionZoningData, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        ...summarySection,
        zoning: jurisdictionDetailsData.zoning.map(
          (zoning: JurisdictionDetailsZoning) => {
            const currentUpdatedZoning = zoningSection.find(
              (updatedZoning: JurisdictionDetailsZoning) =>
                updatedZoning.id === zoning.id,
            );

            if (currentUpdatedZoning) {
              const { id, projectType, projectSubType, ...updatedZoningData } =
                currentUpdatedZoning;

              return {
                ...zoning,
                ...updatedZoningData,
              };
            }

            return zoning;
          },
        ),
      },
    }),
  ),
  // PERMITTING PANEL
  // add jurisdiction permitting panel getJurisdictionPermitting
  on(
    JurisdictionActions.getJurisdictionPermitting,
    ({
      state,
      payload,
    }: PayloadAndState<JurisdictionDetailsPermitting[], PermittingState>) => {
      const permitTypeTablesData = payload.reduce(
        (acc, { id, permittingTypes }) => ({
          ...acc,
          [id]: getProcessedPermitTypes(permittingTypes),
        }),
        {},
      );

      return {
        loadingTabs: { ...state.loadingTabs, permitting: false },
        loadedJurisdictionTabs: {
          ...state.loadedJurisdictionTabs,
          permitting: true,
        },
        jurisdictionDetailsData: {
          ...state.jurisdictionDetailsData,
          permitting: payload,
        },
        originalPermitTypeTableData: permitTypeTablesData,
        currentPermitTypeTableData: permitTypeTablesData,
      };
    },
  ),

  on(
    JurisdictionActions.addJurisdictionPermittingPanel,
    ({
      payload,
      state: {
        jurisdictionDetailsData,
        originalPermitTypeTableData,
        currentPermitTypeTableData,
      },
    }: PayloadAndState<JurisdictionDetailsPermitting, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        permitting: [...jurisdictionDetailsData.permitting, payload],
      },
      originalPermitTypeTableData: {
        ...originalPermitTypeTableData,
        [payload.id]: [],
      },
      currentPermitTypeTableData: {
        ...currentPermitTypeTableData,
        [payload.id]: [],
      },
    }),
  ),
  // update jurisdiction permitting panel
  on(
    JurisdictionActions.updateJurisdictionPermittingPanel,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionModalValues, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        permitting: jurisdictionDetailsData.permitting.map(
          (permitting: JurisdictionDetailsPermitting) =>
            permitting.id !== payload.id
              ? permitting
              : {
                  ...permitting,
                  ...payload,
                },
        ),
      },
    }),
  ),
  // remove jurisdiction permitting panel
  on(
    JurisdictionActions.removeJurisdictionPermittingPanel,
    ({
      payload: id,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<string, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        permitting: [
          ...jurisdictionDetailsData.permitting.filter(
            (permitting: JurisdictionDetailsPermitting) => permitting.id !== id,
          ),
        ],
      },
    }),
  ),
  // JURISDICTION PERMITTING
  // update jurisdiction PERMITTING
  on(
    JurisdictionActions.updateJurisdictionPermitting,
    ({
      payload: { summarySection, permittingSection },
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionPermittingData, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        ...summarySection,
        permitting: jurisdictionDetailsData.permitting.map(
          (permitting: JurisdictionDetailsPermitting) => {
            const currentUpdatedPermitting = permittingSection.find(
              (updatedPermitting: JurisdictionPermittingSectionType) =>
                updatedPermitting.id === permitting.id,
            );

            if (currentUpdatedPermitting) {
              const {
                id,
                projectType,
                projectSubType,
                ...updatedPermittingData
              } = currentUpdatedPermitting;

              return {
                ...permitting,
                ...updatedPermittingData,
              };
            }

            return permitting;
          },
        ),
      },
    }),
  ),

  // JURISDICTION - CONTACTS
  // Load contacts tab data
  on(JurisdictionActions.getJurisdictionContacts, ({ payload, state }) => ({
    ...state,
    loadingTabs: { ...state.loadingTabs, contacts: false },
    loadedJurisdictionTabs: { ...state.loadedJurisdictionTabs, contacts: true },
    jurisdictionDetailsData: {
      ...state.jurisdictionDetailsData,
      contacts: payload,
    },
  })),

  // add jurisdiction contact
  on(
    JurisdictionActions.addJurisdictionContact,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionDetailsContacts, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        contacts: [...jurisdictionDetailsData.contacts, payload],
      },
    }),
  ),
  // update jurisdiction contact
  on(
    JurisdictionActions.updateJurisdictionContact,
    ({
      payload,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<JurisdictionDetailsContacts, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        contacts: jurisdictionDetailsData.contacts.map(
          (contact: JurisdictionDetailsContacts) =>
            contact.id !== payload.id ? contact : { ...contact, ...payload },
        ),
      },
    }),
  ),
  // remove jurisdiction contact
  on(
    JurisdictionActions.removeJurisdictionContact,
    ({
      payload: id,
      state: { jurisdictionDetailsData },
    }: PayloadAndState<string, PermittingState>) => ({
      jurisdictionDetailsData: {
        ...jurisdictionDetailsData,
        contacts: [
          ...jurisdictionDetailsData.contacts.filter(
            (contact: JurisdictionDetailsContacts) => contact.id !== id,
          ),
        ],
      },
    }),
  ),
);
